import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import type { GrantOpportunityEngagementType } from "@/api/grant_opportunities";

type GrantTrackingCreateDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // ENG-1824: false unless the team carries the ai_partner flag, in which case
  // the AI Partner section is hidden entirely and this dialog is unchanged.
  aiPartnerAvailable: boolean;
  aiPartner: boolean;
  onAiPartnerChange: (value: boolean) => void;
  engagementType: GrantOpportunityEngagementType;
  onEngagementTypeChange: (value: GrantOpportunityEngagementType) => void;
  outsideBeneficiary: string;
  onOutsideBeneficiaryChange: (value: string) => void;
  scopeLabel: string;
  title: string;
  onTitleChange: (value: string) => void;
  url: string;
  onUrlChange: (value: string) => void;
  notes: string;
  onNotesChange: (value: string) => void;
  deadline: string;
  onDeadlineChange: (value: string) => void;
  watching: boolean;
  onWatchingChange: (value: boolean) => void;
  saving: boolean;
  onSubmit: () => void;
};

export function GrantTrackingCreateDialog({
  open,
  onOpenChange,
  aiPartnerAvailable,
  aiPartner,
  onAiPartnerChange,
  engagementType,
  onEngagementTypeChange,
  outsideBeneficiary,
  onOutsideBeneficiaryChange,
  scopeLabel,
  title,
  onTitleChange,
  url,
  onUrlChange,
  notes,
  onNotesChange,
  deadline,
  onDeadlineChange,
  watching,
  onWatchingChange,
  saving,
  onSubmit,
}: GrantTrackingCreateDialogProps) {
  // A Tracker opportunity still needs a title. An AI Partner one does not —
  // the package can name it — but it does need a beneficiary once the team
  // says it is supporting someone else.
  const canSubmit = aiPartner
    ? engagementType !== "supporting" || outsideBeneficiary.trim().length > 0
    : title.trim().length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Add grant opportunity</DialogTitle>
          <DialogDescription>
            Add a grant manually. Save it for later or add it straight to your pursuit pipeline.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {aiPartnerAvailable && (
            <div className="space-y-3 rounded-lg border bg-muted/20 px-3 py-3">
              <div className="flex items-start gap-2">
                <Checkbox
                  id="grant-ai-partner"
                  checked={aiPartner}
                  onCheckedChange={(checked) => onAiPartnerChange(checked === true)}
                  className="mt-0.5"
                />
                <div className="space-y-0.5">
                  <Label htmlFor="grant-ai-partner" className="text-sm font-medium">
                    Start with AI Partner
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Upload the opportunity package and let Ordinal draft a plan. You stay the
                    owner and can edit everything before anything is confirmed.
                  </p>
                </div>
              </div>

              {aiPartner && (
                <div className="space-y-3 border-t pt-3">
                  <p className="text-xs text-muted-foreground">
                    Scoped to {scopeLabel}. You can add co-owners and contributors once it is
                    created.
                  </p>

                  <fieldset className="space-y-2">
                    <legend className="text-sm font-medium">Who is this application for?</legend>
                    <div className="flex flex-col gap-2">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="radio"
                          name="grant-engagement-type"
                          value="applying"
                          checked={engagementType === "applying"}
                          onChange={() => onEngagementTypeChange("applying")}
                        />
                        We are applying for it
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="radio"
                          name="grant-engagement-type"
                          value="supporting"
                          checked={engagementType === "supporting"}
                          onChange={() => onEngagementTypeChange("supporting")}
                        />
                        We are supporting someone else&rsquo;s application
                      </label>
                    </div>
                  </fieldset>

                  {engagementType === "supporting" && (
                    <div className="space-y-2">
                      <Label htmlFor="grant-outside-beneficiary">Who are you supporting?</Label>
                      <Input
                        id="grant-outside-beneficiary"
                        value={outsideBeneficiary}
                        onChange={(event) => onOutsideBeneficiaryChange(event.target.value)}
                        placeholder="Organization or community name"
                      />
                      <p className="text-xs text-muted-foreground">
                        Recorded as text for context. It does not create an organization record.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="grant-title">Title</Label>
            <Input
              id="grant-title"
              value={title}
              onChange={(event) => onTitleChange(event.target.value)}
              placeholder="Program or grant name"
            />
            {aiPartner && (
              <p className="text-xs text-muted-foreground">
                Optional — Ordinal can suggest one from the package.
              </p>
            )}
          </div>
          <div className="space-y-2">
            <Label htmlFor="grant-url">URL</Label>
            <Input
              id="grant-url"
              value={url}
              onChange={(event) => onUrlChange(event.target.value)}
              placeholder="https://…"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="grant-deadline">Deadline</Label>
            <Input
              id="grant-deadline"
              type="date"
              value={deadline}
              onChange={(event) => onDeadlineChange(event.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="grant-notes">Notes</Label>
            <Textarea
              id="grant-notes"
              value={notes}
              onChange={(event) => onNotesChange(event.target.value)}
              rows={3}
              placeholder="Internal context for your team…"
            />
          </div>
          <div className="flex items-start gap-2 rounded-lg border bg-muted/20 px-3 py-3">
            <Checkbox
              id="grant-watching"
              checked={watching}
              onCheckedChange={(checked) => onWatchingChange(checked === true)}
              className="mt-0.5"
            />
            <div className="space-y-0.5">
              <Label htmlFor="grant-watching" className="text-sm font-medium">
                Save for later
              </Label>
              <p className="text-xs text-muted-foreground">
                {watching
                  ? "Goes to Saved — evaluate fit before your team commits."
                  : "Goes to Pursuing — starts at Preparing in your application pipeline."}
              </p>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button disabled={!canSubmit || saving} onClick={onSubmit}>
            {saving ? "Saving…" : aiPartner ? "Start with AI Partner" : "Save opportunity"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
